import { useMutation } from '@apollo/client';
import {
  IDashboardInstanceFieldsFragment,
  IDashboardNameEditMutation,
  IDashboardNameEditMutationVariables,
} from '../../../../utils/apollo/graphqlSchema';
import { DASHBOARD_NAME_EDIT } from '../../../../utils/apollo/dashboards';
import { isNil } from 'lodash';
import { MaybeNil } from '../../../../utils/interfaces';
import printGraphqlErrors from '../../../../utils/apollo/printGraphqlErrors';
import { assertNotNil } from '../../../../utils/types';
import { useAppDispatch } from '../../../../redux/hooks';
import { useCallback } from 'react';
import useUpdateDashboardStore from '../useUpdateDashboardStore';
import { useAnalytics } from '../../../../utils/analytics/useAnalytics';
import { dashboardUpdated } from '../../../../utils/analytics/events/dashboard/events';
import useDashboardReload from '../useDashboardReload';
import { reloadDashboardTemplates } from '../../../../redux/slices/dashboards/dashboardInstance';

interface IDashboardEditNameProps {
  newName?: MaybeNil<string>;
  newDescription?: MaybeNil<string>;
}

const useDashboardEditName = (dashboard: IDashboardInstanceFieldsFragment) => {
  const { track } = useAnalytics();
  const dispatch = useAppDispatch();
  const updateDashboardStore = useUpdateDashboardStore();
  const reloadDashboard = useDashboardReload(dashboard.baseId);

  const [editNameMutation] = useMutation<
    IDashboardNameEditMutation,
    IDashboardNameEditMutationVariables
  >(DASHBOARD_NAME_EDIT);

  const editDashboardName = useCallback(
    async ({ newName, newDescription }: IDashboardEditNameProps) => {
      const { data } = await editNameMutation({
        variables: {
          input: {
            baseId: dashboard.baseId,
            instanceId: dashboard.id,
            name: newName ?? dashboard.name,
            description: newDescription ?? dashboard.description,
          },
        },
      });

      const result = data?.dashboardNameEdit;
      if (printGraphqlErrors(result?.errors)) {
        await reloadDashboard();
        return;
      }

      const newDashboard = result?.entity;
      assertNotNil(newDashboard);

      track(dashboardUpdated(newDashboard));
      updateDashboardStore({ dashboard: newDashboard, retainAnalyses: true });

      if (!isNil(newName)) {
        dispatch(reloadDashboardTemplates());
      }
    },
    [
      dashboard.baseId,
      dashboard.description,
      dashboard.id,
      dashboard.name,
      dispatch,
      editNameMutation,
      reloadDashboard,
      track,
      updateDashboardStore,
    ],
  );

  return {
    editDashboardName,
  };
};

export default useDashboardEditName;
